import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import NavItem from "./NavItem";
import useConfig from "@src/hooks/useConfig";
import { IconChevronDown, IconExternalLink } from "@tabler/icons";

const NavCollapse = ({ menu, level }) => {
  const theme = useTheme();
  const { borderRadius } = useConfig();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleClick = () => {
    setOpen(!open);
    setSelected(!open ? menu.id : null);
  };

  const { pathname } = useLocation();
  const checkOpenForParent = (child, id) => {
    child.forEach((item) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  // menu collapse for sub-levels
  useEffect(() => {
    const childrens = menu.children ? menu.children : [];
    childrens.forEach((item) => {
      if (item.children?.length) {
        checkOpenForParent(item.children, menu.id);
      }
      if (pathname && pathname.includes("product-details")) {
        if (item.url && item.url.includes("product-details")) {
          setSelected(menu.id);
          setOpen(true);
        }
      }
      if (item.url === pathname) {
        setSelected(menu.id);
        setOpen(true);
      }
    });
  }, [pathname, menu.children]);

  const menus = menu.children?.map((item) => {
    switch (item.type) {
      case "collapse":
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case "item":
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon
      strokeWidth={1.5}
      size="20px"
      style={{ marginTop: "auto", marginBottom: "auto" }}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: selected === menu.id ? 8 : 6,
        height: selected === menu.id ? 8 : 6,
      }}
      fontSize={level > 0 ? "inherit" : "medium"}
    />
  );

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <ListItemButton
          sx={{
            borderRadius: `${borderRadius}px`,
            mb: 0.5,
            alignItems: "center",
            backgroundColor: level > 1 ? "transparent !important" : "inherit",
            py: level > 1 ? 1 : 1.25,
            pl: `${level * 24}px`,
          }}
          selected={selected === menu.id}
          onClick={handleClick}
        >
          <ListItemIcon sx={{ my: "auto", minWidth: !menu.icon ? 18 : 36 }}>
            {menuIcon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant={selected === menu.id ? "h5" : "body1"}
                color="inherit"
                sx={{ my: "auto" }}
              >
                {menu.title}
              </Typography>
            }
            secondary={
              menu.caption && (
                <Typography
                  variant="caption"
                  sx={{ ...theme.typography.subMenuCaption }}
                  display="block"
                  gutterBottom
                >
                  {menu.caption}
                </Typography>
              )
            }
          />
          <IconChevronDown
            stroke={1.5}
            size="16px"
            style={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "all 0.2s",
            }}
          />
        </ListItemButton>
        <Link to={menu.url}>
          <IconExternalLink stroke={1.5} size="16px" color={theme.palette.grey[700]}/>
        </Link>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {open && (
          <List
            component="div"
            disablePadding
            sx={{
              position: "relative",
              "&:after": {
                content: "''",
                position: "absolute",
                left: "32px",
                top: 0,
                height: "100%",
                width: "1px",
                opacity: theme.palette.mode === "dark" ? 0.2 : 1,
                background:
                  theme.palette.mode === "dark"
                    ? theme.palette.dark.light
                    : theme.palette.primary.light,
              },
            }}
          >
            {menus}
          </List>
        )}
      </Collapse>
    </>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number,
};

export default NavCollapse;
